import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export const AgreementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const RowLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const RowInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const RowTitle = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

export const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ContractTypeTag = styled.span`
  ${TypographyV2.s1}
  padding: 4px 10px;
  border-radius: 9999px;
  font-weight: 500;
  background-color: ${ColorV2.Blue5};
  color: ${ColorV2.Blue1};
`;

export const StatusPill = styled.span<{ $status: string }>`
  ${TypographyV2.s1}
  padding: 4px 10px;
  border-radius: 9999px;
  font-weight: 500;
  text-transform: capitalize;

  ${({ $status }) => {
    switch ($status.toLowerCase()) {
      case "completed":
        return `
          background-color: ${ColorV2.PositiveGreenLight};
          color: ${ColorV2.BookedGreen};
        `;
      case "created":
      case "pending":
        return `
          background-color: #FFF3CD;
          color: #856404;
        `;
      case "expired":
        return `
          background-color: ${ColorV2.NegativeRedLight};
          color: ${ColorV2.NegativeRed};
        `;
      default:
        return `
          background-color: #E2E3E5;
          color: #383D41;
        `;
    }
  }}
`;

export const CopyToast = styled.div`
  background: ${ColorV2.PositiveGreenLight};
  color: ${ColorV2.BookedGreen};
  padding: 10px 16px;
  border-radius: 8px;
  ${TypographyV2.b2}
  font-weight: 500;
  text-align: center;
  margin-bottom: 12px;
`;

export const CreateModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
`;

export const CreateModalScroll = styled.div`
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* Custom scrollbar to make it visually clear that scrolling is available */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue6};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue3};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue2};
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

export const AccordionItem = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background-color: ${ColorV2.White};
`;

export const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  cursor: pointer;
  background-color: ${ColorV2.White};
`;

export const AccordionBody = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${ColorV2.White};
  gap: 12px;
  overflow: hidden;
  transition:
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s ease-in-out,
    padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  max-height: ${({ $isOpen }) => ($isOpen ? "500px" : "0px")};
  opacity: ${({ $isOpen }) => ($isOpen ? "1" : "0")};
  padding: ${({ $isOpen }) => ($isOpen ? "0 18px 18px 18px" : "0 18px")};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
`;

export const ParticipantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

export const ParticipantRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background-color: ${ColorV2.White};
  border-radius: 16px;
`;

export const PersonCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const AvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${ColorV2.Blue6};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

export const PersonText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PersonName = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PersonNumber = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue3};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

export const ExpandIconWrapper = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${({ $isExpanded }) =>
    $isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
`;

export const SignaturesRatio = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue3};
  font-weight: 600;
`;

export const TransparentBox = styled.div`
  background: transparent;
  padding: 0;
`;

export const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  font-family: inherit;
  font-size: 14px;
  color: ${ColorV2.Blue1};
  padding: 8px 12px;
  background: ${ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  border-radius: 8px;
  resize: vertical;
  outline: none;
  &:focus {
    border-color: ${ColorV2.Blue2};
  }
`;

export const ProbationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const ProbationInputs = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;

  > div:first-child {
    flex: 1;
  }

  > div:last-child {
    flex: 1.2;
  }
`;

export const FieldHelpText = styled.div`
  font-size: 12px;
  color: ${ColorV2.Blue3};
  margin-top: 4px;
`;

export const CalculatedSalaryNote = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${ColorV2.Blue2};
  background-color: ${ColorV2.Blue5};
  padding: 6px 10px;
  border-radius: 6px;
  margin-top: 4px;
`;

export const SpecialTermsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SpecialTermsLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${ColorV2.Blue1};
`;

export const SpecialTermsHelpText = styled.div`
  font-size: 13px;
  color: ${ColorV2.Blue3};
  line-height: 1.4;
`;

export const SpecialTermsInfo = styled.div`
  font-size: 12px;
  color: ${ColorV2.Blue4};
  font-style: italic;
  margin-top: 2px;
`;


